import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { routes, token_keys } from '../../constants';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import api from '../../api';
import StyledPage from '../../components/StyledPage';
import useStore from "../../store";


export default function Login({ authentication }) {
  const defaultForm = {
    username: '',
    password: '',
    client_id: process.env.REACT_APP_CLIENT_ID,
    grant_type: 'password',
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // branding panel hidden below md

  const [userData, setUserData] = useState(defaultForm);
  const [inputErrors, setInputErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email'); // 'email' | 'password'
  const setActiveUser = useStore((state) => state.setActiveUser);
  const setAuthenticated = useStore((state) => state.setAuthenticated);

  // Auth failures (WorkOS / Entra) bounce back here as ?e=... (e.g.
  // user_not_found). Surface that message on the email field.
  useEffect(() => {
    const reason = searchParams.get('e') || searchParams.get('error');
    if (reason) {
      setInputErrors({ username: reason.replace(/_/g, ' ') });
    }
  }, [searchParams]);

  const modifyFormData = (property, event) => {
    const dataCopy = { ...userData };
    dataCopy[property] = event.target.value;
    setUserData(dataCopy);
  };

  const resolveAuthMethod = (data) => {
    if (typeof data === 'string') return data;
    return data?.auth_method || data?.method || data?.type;
  };

  const resolveSsoUrl = (data) => {
    if (typeof data === 'string') return data;
    return data?.redirect_url || data?.url || data?.sso_url;
  };

  const continueWithEmail = async (event) => {
    event.preventDefault();

    const email = userData.username.trim();
    if (email === '') {
      setInputErrors({ username: 'Email is required' });
      return;
    }

    setLoading(true);
    try {
      const lookup = await api.authLookup(email);
      if (!lookup.success) {
        setInputErrors({ username: lookup.error || 'Unable to look up account' });
        return;
      }

      const method = resolveAuthMethod(lookup.data);

      if (method === 'entra') {
        // Internal @prompta.ai employees. The backend already built the
        // OIDC start URL (with state/PKCE pinned to its session) and returns
        // it as `sso_start_url`. We must full-page navigate to it — a fetch
        // would drop the cookies the backend just set, so never call it via XHR.
        const startUrl = lookup.data?.sso_start_url || resolveSsoUrl(lookup.data);
        if (startUrl) {
          window.location.assign(startUrl);
          return;
        }
        setInputErrors({ username: 'Entra sign-in URL not provided' });
        return;
      }

      if (method === 'sso') {
        const ssoResp = await api.ssoStart(email);
        if (!ssoResp.success) {
          setInputErrors({ username: ssoResp.error || 'Unable to start SSO' });
          return;
        }
        const redirectUrl = resolveSsoUrl(ssoResp.data);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        setInputErrors({ username: 'SSO redirect URL not provided' });
        return;
      }

      if (method === 'password') {
        setInputErrors({});
        setStep('password');
        return;
      }

      setInputErrors({ username: 'Unknown authentication method' });
    } catch (error) {
      setInputErrors({ username: 'Unable to look up account' });
    } finally {
      setLoading(false);
    }
  };

  const login = async (event) => {
    event.preventDefault();

    let errors = {};
    if (userData.username.trim() === '') {
      errors = { username: 'Username is required' };
    }
    if (userData.password.trim() === '') {
      errors = { ...errors, password: <FormattedMessage id="login.password_required" /> };
    }
    if (Object.keys(errors).length !== 0) {
      setInputErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const loginStatus = await api.login(userData);
      if (Object.keys(loginStatus.data).includes('access_token')) {
        const accessToken = loginStatus.data['access_token'];
        const refreshToken = loginStatus.data['refresh_token'];

        const expiresIn = loginStatus.data['expires_in'] || 900;
        const issuedAt = Date.now();
        localStorage.setItem(token_keys.expiration, expiresIn.toString());
        localStorage.setItem(token_keys.issued_at, issuedAt.toString());
        localStorage.setItem(token_keys.access_token, accessToken);
        localStorage.setItem(token_keys.refresh_token, refreshToken);
        // Logging in is activity: start the idle clock now so the new session
        // can't inherit a stale stamp from a previous one.
        localStorage.setItem(token_keys.last_activity, issuedAt.toString());
        const userResponse = await api.getCurrentUser();
        setActiveUser(userResponse.data);
        setAuthenticated(true);
        authentication(true);
        navigate(routes.home)
      } else {
        setInputErrors({
          username: '',
          password: <FormattedMessage id="login.incorrect_credentials" />,
        });
      }
    } catch (error) {
      setInputErrors({
        username: '',
        password: <FormattedMessage id="login.incorrect_credentials" />,
      });
    } finally {
      setLoading(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setInputErrors({});
    setUserData({ ...userData, password: '' });
  };

  return (
    <StyledPage>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isMobile && (
          <Box
            component="img"
            src="/logo.png"
            alt="Prompta.AI"
            sx={{ height: 40, width: 'auto', mb: 4 }}
          />
        )}
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            color: '#00438E',
            textAlign: 'center',
            fontWeight: 700,
          }}
        >
          <FormattedMessage id="login.sign_in" />
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 4, color: '#6B7280', textAlign: 'center' }}
        >
          <FormattedMessage id="login.not_registered" />{' '}
          <Link
            onClick={() => navigate(routes.register)}
            sx={{
              color: '#00438E',
              fontWeight: 600,
              '&:hover': { color: '#4A87C8' },
              cursor: 'pointer',
            }}
          >
            <FormattedMessage id="register.register" />
          </Link>
        </Typography>
        <Box
          component="form"
          onSubmit={step === 'email' ? continueWithEmail : login}
          sx={{ width: '100%' }}
        >
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              error={Object.keys(inputErrors).includes('username')}
              helperText={inputErrors['username'] || ''}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              type="text"
              label={<FormattedMessage id="login.user_or_email" />}
              value={userData.username}
              onChange={(event) => modifyFormData('username', event)}
              disabled={step === 'password'}
              sx={{
                '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                '& .MuiInputLabel-root': { color: '#00438E' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#00438E' },
                  '&:hover fieldset': { borderColor: '#4A87C8' },
                  '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                },
              }}
            />
          </Box>
          {step === 'password' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                autoFocus
                error={Object.keys(inputErrors).includes('password')}
                helperText={inputErrors['password'] || ''}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                type="password"
                label={<FormattedMessage id="register.password_r" />}
                value={userData.password}
                onChange={(event) => modifyFormData('password', event)}
                sx={{
                  '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                  '& .MuiInputLabel-root': { color: '#00438E' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00438E' },
                    '&:hover fieldset': { borderColor: '#4A87C8' },
                    '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                  },
                }}
              />
            </Box>
          )}
          {step === 'password' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Link
                onClick={backToEmail}
                sx={{
                  color: '#00438E',
                  '&:hover': { color: '#4A87C8' },
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Use a different email
              </Link>
              <Link
                onClick={() => navigate(routes.forgotPassword)}
                sx={{
                  color: '#00438E',
                  '&:hover': { color: '#4A87C8' },
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <FormattedMessage id="login.forgot_password" />
              </Link>
            </Box>
          )}
          <Box sx={{ color: 'crimson', mb: 2, textAlign: 'center' }}>
            {Object.keys(inputErrors).includes('error') ? inputErrors['error'] : ''}
          </Box>
          <Button
            size="large"
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              width: '100%',
              backgroundColor: '#00438E',
              '&:hover': { backgroundColor: '#003A75' },
              py: 1.5,
              mt: 1,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : step === 'email' ? (
              'Continue'
            ) : (
              <FormattedMessage id="login.sign_in" />
            )}
          </Button>
        </Box>
      </Box>
    </StyledPage>
  );
}